// @ts-nocheck
import { useState } from "react";
import { Bot, CheckCircle, XCircle, ChevronDown, AlertTriangle, Loader2 } from "lucide-react";
import { formatMessageTimestamp } from "@/lib/chat-utils";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";

interface ActionConfirmationBubbleProps {
  message: {
    id: string;
    senderId: string;
    text?: string | null;
    metadata?: {
      pendingToolCallId: string;
      toolName: string;
      toolArgs: Record<string, any>;
      status: 'pending' | 'approved' | 'declined';
    };
    createdAt: string | Date;
    sender?: { firstName?: string; lastName?: string } | null;
  };
  agentName?: string;
  onApprove: (pendingToolCallId: string) => void;
  onDecline: (pendingToolCallId: string) => void;
  isLoading?: boolean;
}

export function ActionConfirmationBubble({ message, agentName, onApprove, onDecline, isLoading }: ActionConfirmationBubbleProps) {
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const meta = message.metadata;
  const status = meta?.status || 'pending';
  const senderName = agentName || message.sender?.firstName || 'Agent';

  return (
    <div className="flex justify-start group" data-testid={`message-${message.id}`}>
      <div className="max-w-[70%]">
        {/* Agent name + bot badge */}
        <div className="text-xs font-semibold mb-1 flex items-center gap-1 px-1">
          {senderName}
          <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold uppercase tracking-wider text-primary bg-primary/10 px-1 py-0.5 rounded">
            <Bot className="h-2.5 w-2.5" />
            Bot
          </span>
        </div>

        <Card className="border-amber-200 bg-amber-50/50 dark:border-amber-800 dark:bg-amber-950/30 p-3">
          {/* Summary */}
          <div className="flex items-start gap-2 mb-2">
            <AlertTriangle className="h-4 w-4 text-amber-500 mt-0.5 shrink-0" />
            <div className="text-sm font-medium">{message.text || meta?.toolName}</div>
          </div>

          {/* Tool name badge */}
          <div className="mb-2">
            <span className="text-[10px] font-mono bg-muted px-1.5 py-0.5 rounded text-muted-foreground">
              {meta?.toolName}
            </span>
          </div>

          {/* Collapsible details */}
          {meta?.toolArgs && Object.keys(meta.toolArgs).length > 0 && (
            <Collapsible open={isDetailsOpen} onOpenChange={setIsDetailsOpen}>
              <CollapsibleTrigger className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors mb-2">
                <ChevronDown className={`h-3 w-3 transition-transform ${isDetailsOpen ? 'rotate-180' : ''}`} />
                Details
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className="text-xs bg-muted/50 rounded p-2 space-y-1 mb-2">
                  {Object.entries(meta.toolArgs).map(([key, value]) => (
                    <div key={key} className="flex gap-2">
                      <span className="font-mono text-muted-foreground">{key}:</span>
                      <span className="break-all">{typeof value === 'object' ? JSON.stringify(value) : String(value)}</span>
                    </div>
                  ))}
                </div>
              </CollapsibleContent>
            </Collapsible>
          )}

          {/* Action buttons or status badge */}
          {status === 'pending' && (
            <div className="flex items-center gap-2 mt-1">
              <Button
                size="sm"
                variant="default"
                className="h-7 bg-green-600 hover:bg-green-700 text-white"
                onClick={() => meta?.pendingToolCallId && onApprove(meta.pendingToolCallId)}
                disabled={isLoading}
                data-testid={`approve-action-${message.id}`}
              >
                {isLoading ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <CheckCircle className="h-3.5 w-3.5 mr-1" />}
                Approve
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="h-7 border-red-300 text-red-600 hover:bg-red-50 dark:border-red-700 dark:text-red-400 dark:hover:bg-red-950"
                onClick={() => meta?.pendingToolCallId && onDecline(meta.pendingToolCallId)}
                disabled={isLoading}
                data-testid={`decline-action-${message.id}`}
              >
                <XCircle className="h-3.5 w-3.5 mr-1" />
                Decline
              </Button>
            </div>
          )}

          {status === 'approved' && (
            <div className="flex items-center gap-1 mt-1">
              <CheckCircle className="h-3.5 w-3.5 text-green-600" />
              <span className="text-xs font-medium text-green-600">Approved</span>
            </div>
          )}

          {status === 'declined' && (
            <div className="flex items-center gap-1 mt-1">
              <XCircle className="h-3.5 w-3.5 text-red-500" />
              <span className="text-xs font-medium text-red-500">Declined</span>
            </div>
          )}
        </Card>

        {/* Timestamp */}
        <div className="text-xs text-muted-foreground mt-1 px-1">
          {formatMessageTimestamp(message.createdAt)}
        </div>
      </div>
    </div>
  );
}
